import React, { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// import * as Sentry from "@sentry/react";
import { lazy } from "react";
import AppProvider from "./AppContext";
import LoadingScreen from "./components/loader/LoadingScreen";
import MobileBottomNav from "./components/navBottom";
import { history } from "./helpers/history";
import { RootState } from "./store";
import { isNullOrUndefined } from "./utils";
import { ResponseData } from "./model/Responses";
import { checkToken } from "./services/player.service";
import { logoutAction } from "./actions/auth";
import useClientWebsite from "./components/getClientWebsite";
import config from "./config";
import Footer from "./components/footer";

const Dashboard = lazy(() => import("./views/Dashboard"));
const Lobby = lazy(() => import("./views/Lobby"));
const Download = lazy(() => import("./views/Download"));
// const SupportModal = lazy(() => import("./components/SupportModal"));
const Promotion = lazy(() => import("./views/Promotion"));

// Sentry.init({
// 	dsn: "https://4e748ab9e202ee6dc77f9e4dd018c28e@o4508758524428288.ingest.de.sentry.io/4509410193899600",
// 	integrations: [new Sentry.BrowserTracing()],
// 	tracesSampleRate: 1.0,
// });

const App: React.FC = () => {
	const token = sessionStorage.getItem("user_token");
	const { i18n } = useTranslation();
	const { loading, language } = useSelector((state: RootState) => state.store);
	const { userProfile } = useSelector((state: any) => state.userProfile);
	const dispatch = useDispatch();
	const [themeLoading, setThemeLoading] = useState<boolean>(true);
	const [clientName] = useState("");
	//here will be replaced by using detection on the device user use
	//to return the specific theme selected by user for the specific device

	const Sidebar = lazy(() =>
		import(`./components/client/${config.client}/common/sidebar`).catch(() => {
			return import("./components/menu/sidebar");
		})
	);
	const Header = lazy(() =>
		import(`./components/client/${config.client}/common/header`).catch(() => {
			return import("./components/header");
		})
	);

	const routes = [
		{
			path: "/",
			exact: true,
			name: "Home",
			component: !isNullOrUndefined(userProfile) ? Lobby : Dashboard,
			access: true,
		},
		{
			path: "/promotion",
			name: "Promotion",
			component: Promotion,
			access: true,
		},
		{ path: "/home", name: "Home", component: Dashboard, access: true },
		{
			path: "/lobby",
			name: "Lobby",
			component: Lobby,
			access: !isNullOrUndefined(userProfile),
		},
		{ path: "/download", name: "Download", component: Download, access: true },
	];

	useClientWebsite(clientName, setThemeLoading);

	useEffect(() => {
		if (i18n.language !== language) i18n.changeLanguage(language);
	}, [language]);

	useEffect(() => {
		if (!token) return;
		const interval = setInterval(async () => {
			try {
				const result: ResponseData<any> = await checkToken(token);
				if (!result.status) {
					toast.error(result.message);
					dispatch(logoutAction());
					return;
				}
			} catch (error) {
				console.error("Token validation error:", error);
				dispatch(logoutAction());
			}
		}, 300000); // Poll every 5 minutes

		return () => clearInterval(interval);
	}, [token]);

	const menu = routes.map((route, index) => {
		return route.component && route.access === true ? (
			<Route
				key={index}
				path={route.path}
				exact={route.exact}
				render={(props: any) => <route.component {...props} />}
			/>
		) : null;
	});

	return (
		<div className="wrapper">
			<Router history={history}>
				<AppProvider>
					<Suspense fallback={<LoadingScreen loading={loading} />}>
						{loading && <LoadingScreen loading={loading} />}
						{themeLoading && <LoadingScreen loading={themeLoading} />}
						<Sidebar />
						{/* <SupportModal /> */}
						<div className="sidebar-right">
							<Header />
							<Switch>{menu}</Switch>
						</div>
					</Suspense>
					<ToastContainer autoClose={3000} theme="dark" />
					<MobileBottomNav />
					{/* <Footer /> */}
				</AppProvider>
			</Router>
		</div>
	);
};

export default App;
